import {
  LOAD_PROJECT_ANALYSIS_REPORT,
  ERROR,
  FILTER_PROJECT_ANALYSIS_REPORT,
  UPDATE_FILTERS_APPLIED,
  UNPIN_CLIP,
  CREATE_NEW_HIGHLIGHT_REEL,
  ADD_CLIP_TO_HIGHLIGHT_REEL,
  REMOVE_CLIP_FROM_HIGHLIGHT_REEL,
  REORDER_CLIPS_IN_HIGHLIGHT_REEL,
  DOWNLOAD_HIGHLIGHT_REEL,
  GET_DOWNLOAD_HIGHLIGHT_REEL_STATUS,
  RESET_PROJECT_ANALYSIS_REPORT,
  DELETE_HIGHLIGHT_REEL,
  ADD_INSIGHT,
  UPDATE_INSIGHT,
  DELETE_INSIGHT,
  UPDATE_INTEGRATIONS,
  UPDATE_CLIP_STATE,
} from "../actions/types";
import {
  FILTER_SPACE_BY_RESPONDENT_SESSION,
  FILTER_SPACE_BY_SEARCH_KEYWORDS,
  FILTER_SPACE_BY_TAGS,
  REEL_READY,
  ANALYSIS_TYPE_VIDEO_CLIP,
} from "../actions/constants";
import { getValidJSON } from "../utills/common";

const initialState = {
  error: null,
  referrer: null,
  isLoading: true,
  apiData: null,
  renderingData: {},
  filtersApplied: {
    FILTER_SPACE_BY_RESPONDENT_SESSION: 0,
    FILTER_SPACE_BY_SEARCH_KEYWORDS: "",
    FILTER_SPACE_BY_TAGS: [],
  },
  integrations: {
    showDropdown: false,
    showDrawer: false,
    showMiroDrawer: false,
    oauthStarted: false,
    showExportDropdown: false,
    synching: false,
    connectLoading: false,
    notificationData: null,
  },
  miroIntegration: null,
  isSlackIntegrated: false,
};

const projectAnalysisReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case RESET_PROJECT_ANALYSIS_REPORT:
      return { ...state, isLoading: true, error: null, ...initialState };

    case LOAD_PROJECT_ANALYSIS_REPORT:
      return { ...state, isLoading: false, error: null, ...payload };

    case UNPIN_CLIP: {
      const unpinClipId = payload.analysisSpaceModel.id;
      const renderingData = state.renderingData.filter((s) => Number(s.id) !== Number(unpinClipId));
      const space = Object.fromEntries(Object.entries(state.apiData.space).filter(([, s]) => Number(s.id) !== Number(unpinClipId)));

      const updatedReels = payload.highlightReelsModel?.filter((reel) => !reel.error) || [];
      const highlightReels = state.apiData.highlightReels.map((r) => {
        const updated = updatedReels.find((reel) => Number(reel.model.id) === Number(r.id));
        return updated ? { ...r, space_ids: updated.model.space_ids, status: updated.model.status } : r;
      });

      return { ...state, isLoading: false, error: null, renderingData, apiData: { ...state.apiData, space, highlightReels } };
    }

    case CREATE_NEW_HIGHLIGHT_REEL:
      return {
        ...state,
        isLoading: false,
        error: null,
        apiData: { ...state.apiData, highlightReels: [payload.data, ...state.apiData.highlightReels] },
      };

    case ADD_INSIGHT:
      return {
        ...state,
        isLoading: false,
        error: null,
        apiData: { ...state.apiData, insights: [payload.data, ...state.apiData.insights] },
      };

    case UPDATE_INSIGHT: {
      const insights = state.apiData.insights.map((i) => (Number(i.id) === Number(payload.data.id) ? payload.data : i));
      return { ...state, isLoading: false, error: null, apiData: { ...state.apiData, insights } };
    }

    case DELETE_INSIGHT: {
      const insights = state.apiData.insights.filter((i) => Number(i.id) !== Number(payload.data.id));
      return { ...state, isLoading: false, error: null, apiData: { ...state.apiData, insights } };
    }

    case DELETE_HIGHLIGHT_REEL: {
      if (payload.data.error != null) return { ...state, isLoading: false, error: null };
      const highlightReels = state.apiData.highlightReels.filter((r) => Number(r.id) !== Number(payload.data.model.id));
      return { ...state, isLoading: false, error: null, apiData: { ...state.apiData, highlightReels } };
    }

    case ADD_CLIP_TO_HIGHLIGHT_REEL:
    case REMOVE_CLIP_FROM_HIGHLIGHT_REEL:
    case REORDER_CLIPS_IN_HIGHLIGHT_REEL:
    case DOWNLOAD_HIGHLIGHT_REEL: {
      if (payload.data.error != null) return { ...state, isLoading: false, error: null };
      // Immutable update so the provider's context memo (keyed on apiData) re-renders
      const highlightReels = state.apiData.highlightReels.map((r) =>
        Number(r.id) === Number(payload.data.model.id)
          ? { ...r, space_ids: payload.data.model.space_ids, status: payload.data.model.status }
          : r
      );
      return { ...state, isLoading: false, error: null, apiData: { ...state.apiData, highlightReels } };
    }

    case GET_DOWNLOAD_HIGHLIGHT_REEL_STATUS: {
      const readyById = new Map(payload.data.map((reel) => [Number(reel.id), reel]));
      const highlightReels = state.apiData.highlightReels.map((r) =>
        readyById.has(Number(r.id)) ? { ...r, status: REEL_READY, url: readyById.get(Number(r.id)).url } : r
      );
      return { ...state, isLoading: false, error: null, apiData: { ...state.apiData, highlightReels } };
    }

    case UPDATE_FILTERS_APPLIED:
      const { updateFilterType, updateFilterKey } = payload;
      if (updateFilterType === FILTER_SPACE_BY_RESPONDENT_SESSION) {
        return {
          ...state,
          isLoading: false,
          error: null,
          filtersApplied: { ...state.filtersApplied, FILTER_SPACE_BY_RESPONDENT_SESSION: updateFilterKey },
        };
      } else if (updateFilterType === FILTER_SPACE_BY_SEARCH_KEYWORDS) {
        return {
          ...state,
          isLoading: false,
          error: null,
          filtersApplied: { ...state.filtersApplied, FILTER_SPACE_BY_SEARCH_KEYWORDS: updateFilterKey },
        };
      } else if (updateFilterType === FILTER_SPACE_BY_TAGS) {
        return {
          ...state,
          isLoading: false,
          error: null,
          filtersApplied: { ...state.filtersApplied, FILTER_SPACE_BY_TAGS: updateFilterKey },
        };
      }
      break;

    case FILTER_PROJECT_ANALYSIS_REPORT:
      let filteredData = Object.values(state.apiData.space);
      Object.keys(state.filtersApplied).forEach((filterType) => {
        const filterKey = state.filtersApplied[filterType];
        if (filterType === FILTER_SPACE_BY_RESPONDENT_SESSION) {
          filteredData = Number(filterKey) === 0 ? filteredData : filteredData.filter((s) => s.session_id === filterKey);
        } else if (filterType === FILTER_SPACE_BY_SEARCH_KEYWORDS) {
          filteredData =
            filterKey === ""
              ? filteredData
              : filteredData.filter((s) => {
                  const d = JSON.parse(s.data);
                  return (
                    (d.comment && d.comment.toLowerCase().indexOf(filterKey) !== -1) ||
                    (d.user_type && d.user_type.toLowerCase().indexOf(filterKey) !== -1) ||
                    (d.user_name && d.user_name.toLowerCase().indexOf(filterKey) !== -1) ||
                    (d.destination_file && d.destination_file.toLowerCase().indexOf(filterKey) !== -1) ||
                    state.apiData.session
                      ?.find((x) => x.id === s.session_id)
                      ?.name.toLowerCase()
                      .indexOf(filterKey) !== -1 ||
                    state.apiData.session
                      .find((x) => x.id === s.session_id)
                      ?.participant_name.toLowerCase()
                      .indexOf(filterKey) !== -1
                  );
                });
        } else if (filterType === FILTER_SPACE_BY_TAGS) {
          filteredData =
            filterKey.length === 0
              ? filteredData
              : filteredData.filter((s) => {
                  let tags = [];
                  if (s.ranges && s.ranges.length) {
                    s.ranges.forEach((r) => {
                      r.tags.split(",").forEach((t) => {
                        if (tags.indexOf(t) === -1) tags.push(t);
                      });
                    });
                  } else if (JSON.parse(s.data)) {
                    tags = JSON.parse(s.data)?.tags?.split(",");
                  }
                  return filterKey.some((e) => tags?.includes(e));
                });
        }
      });
      return { ...state, isLoading: false, error: null, renderingData: filteredData };

    case UPDATE_CLIP_STATE: {
      const { clipId, state: clipState } = payload;
      // Patch every clip entry for this clip (it can appear more than once — pinned, notPinned,
      // inside a reel). Match on ref_id or the id inside data; keep references immutable.
      const patchItem = (item) => {
        if (String(item?.type) !== ANALYSIS_TYPE_VIDEO_CLIP) return item;
        const parsed = getValidJSON(item?.data);
        if (!parsed) return item;
        const isMatch = Number(item?.ref_id) === Number(clipId) || Number(parsed.id) === Number(clipId);
        if (isMatch) {
          return { ...item, data: JSON.stringify({ ...parsed, is_clip_ready: Number(clipState) }) };
        }
        return item;
      };

      const renderingData = Array.isArray(state.renderingData) ? state.renderingData.map(patchItem) : state.renderingData;

      let apiData = state.apiData;
      if (apiData?.space) {
        const space = Object.fromEntries(Object.entries(apiData.space).map(([key, value]) => [key, patchItem(value)]));
        apiData = { ...apiData, space };
      }

      return { ...state, isLoading: false, error: null, renderingData, apiData };
    }

    case UPDATE_INTEGRATIONS:
      const { integrations, miroIntegration, isLoading, isSlackIntegrated } = payload;

      return {
        ...state,
        error: null,
        miroIntegration: { ...state.miroIntegration, ...miroIntegration },
        integrations: {
          ...state.integrations,
          ...integrations,
          notificationData: integrations?.notificationData ?? null,
        },
        isSlackIntegrated: isSlackIntegrated ?? state?.isSlackIntegrated,
        isLoading: isLoading ?? state.isLoading,
      };

    case ERROR:
      return { ...state, isLoading: false, error: payload };

    default:
      return state;
  }
};

export default projectAnalysisReducer;
